import {getCookie, setCookie} from '@n2-common/lib/cookie';
import {WindowWithDataLayer} from '@n2-common/lib/gtm';
import {getGtmServerSideId, getGtmServerSideUrl, getLoadGtmWithoutCookieCheck ,getNonceId} from '@n2-common/lib/serverSideConfigs';
import {Button, Headline, Spinner} from '@n2-common/ui';
import {Suspense, useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import links from '../../external-links.json';
import style from './style.module.scss';
import {joinNames} from '@n2-common/ui/helpers';
import {Loader} from '@n2-common/myPagesUi/components/loader/Loader';

declare const window: WindowWithDataLayer;

function loadGtmScript(id: string, gtmScriptUrl: string) {
    const w = window;
    const d = document;
    const s = 'script';
    const l = 'dataLayer';
    w[l] = w[l] || [];
    w[l].push({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js',
    });
    const f = d.getElementsByTagName(s)[0],
        j = d.createElement(s),
        dl = l != 'dataLayer' ? '&l=' + l : '';

    const nonceId = getNonceId();

    j.async = true;
    j.src = `${gtmScriptUrl}?id=` + id + dl;
    
    j.setAttribute('nonce', nonceId || '');
    f?.parentNode?.insertBefore(j, f);
}

function pushPreDatalayerEvents() {
    const w = window;
    const l = 'dataLayer';
    setTimeout(() => {
        if (window.preDataLayer && window.preDataLayer.length > 0) {
            //push events added before GTM was loaded
            for (const evnt of window.preDataLayer) {
                w[l].push(evnt);
            }
        }
    }, 3000);
}

type CookieConsentProps = {
    country: Country;
    position?: 'Fixed' | 'Sticky';
};

const cookieNamesThatRequiresGTM = ['CookieConsentMarketing', 'CookieConsentPreference', 'CookieConsentStatistics'];
const cookieNameRequired = 'CookieConsentRequired';
type consentLevel = 'NotSet' | 'Required' | 'Marketing';

function getConsentsFromCookie() {
    for (const index in cookieNamesThatRequiresGTM) {
        const str = getCookie(cookieNamesThatRequiresGTM[index]);
        if (str && str === '1') {
            return 'Marketing';
        }
    }
    const requiredcookie = getCookie(cookieNameRequired);
    if (requiredcookie && requiredcookie === '1') {
        return 'Required';
    } else {
        return 'NotSet';
    }
}

/**
 * Handles cookieconsent and GTM
 * @param {CookieConsentProps} props
 * @returns {JSX.Element}
 */
export const CookieConsent = ({country, position = 'Fixed'}: CookieConsentProps): JSX.Element => {
    const {t} = useTranslation('common');

    const [consentLevelGiven, setBasicCookieConcentGiven] = useState<consentLevel>(getConsentsFromCookie());
    const [loadGtmWithoutCookieCheck] = useState<boolean>(getLoadGtmWithoutCookieCheck());
    
    const setConcentCookies = useCallback((acceptCookies: boolean) => {
        const cookieVal = acceptCookies ? '1' : '0';
        for (const index in cookieNamesThatRequiresGTM) {
            setCookie(cookieNamesThatRequiresGTM[index], cookieVal, 1000);
        }
        setCookie(cookieNameRequired, '1', 1000);
        setBasicCookieConcentGiven(acceptCookies ? 'Marketing' : 'Required');
    }, []);

    useEffect(() => {
        if (consentLevelGiven === 'Marketing' || loadGtmWithoutCookieCheck) {
            const serverSideGtmUri = getGtmServerSideUrl();
            if (serverSideGtmUri.length > 1) {
                loadGtmScript(getGtmServerSideId() || '', serverSideGtmUri);
            }
            pushPreDatalayerEvents();
        }
    }, [consentLevelGiven, loadGtmWithoutCookieCheck]);
    
    if (loadGtmWithoutCookieCheck) {
        return <></>
    }
    
    return consentLevelGiven === 'NotSet' ? (
        <div className={joinNames(style.cookieBox, position === 'Sticky' ? style.stickyCookieBox : '')}>
            <div className={style.inner}>
                <Headline level={4}>{t<string>('CookieConsent.Headline')}</Headline>
                <p>{t<string>('CookieConsent.Body')}</p>
                <a href={getConsentUrl(country)}>{t<string>('CookieConsent.ReadMore')}</a>
                <div className={style.buttons}>
                    <Button uiTestingId="AcceptCookiesButton" onClick={() => setConcentCookies(true)}>
                        {t<string>('CookieConsent.Accept')}
                    </Button>
                    <a
                        href="#"
                        target="_blank"
                        onClick={(e) => {
                            e.preventDefault();
                            setConcentCookies(false);
                        }}
                    >
                        {t<string>('CookieConsent.No')}
                    </a>
                </div>
            </div>
        </div>
    ) : (
        <span data-nameforuitesting="cookieconsent_given" hidden></span>
    );
};

export function getConsentUrl(country: Country) {
    switch (country) {
        case 'NO':
            return links['COOKIE_CONSENT.NO'].url;
        case 'FI':
            return links['COOKIE_CONSENT.FI'].url;
        case 'SE':
            return links['COOKIE_CONSENT.SE'].url;

        default:
            throw new Error(`getConsentUrl('${country}') not implemented.`);
    }
}

export default function LazyCookieConsent(props: CookieConsentProps) {
    return (
        <Suspense fallback={<Spinner text="Loading language..."/>}>
            <CookieConsent {...props} />
        </Suspense>
    );
}

export function LazyCookieConsentWithDelayedSpinner(props: CookieConsentProps) {
    return (
        <Suspense fallback={<Loader/>}>
            <CookieConsent {...props} />
        </Suspense>
    );
}
